import React, { useState } from "react";
import { Plus, Check, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NEW_VALUE = "__create_new__";

export const CreatableSelect = ({
  value, onChange, options = [], placeholder = "Seleccionar", createLabel = "Agregar nuevo…",
  onCreate, disabled = false, className = "", testId,
}) => {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const allOptions = value && !options.includes(value) ? [value, ...options] : options;

  const submit = async () => {
    const name = draft.trim();
    if (!name) return;
    setSaving(true);
    try {
      const created = await onCreate(name);
      if (created !== false) {
        onChange(created || name);
        setCreating(false);
        setDraft("");
      }
    } finally {
      setSaving(false);
    }
  };

  if (creating) {
    return (
      <div className="flex items-center gap-1" data-testid={testId ? `${testId}-create` : undefined}>
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Nuevo…"
          className="h-8 text-xs"
          disabled={saving}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); submit(); }
            if (e.key === "Escape") { setCreating(false); setDraft(""); }
          }}
          data-testid={testId ? `${testId}-create-input` : undefined}
        />
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={submit} disabled={saving || !draft.trim()} data-testid={testId ? `${testId}-create-confirm` : undefined}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-emerald-600" />}
        </Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => { setCreating(false); setDraft(""); }} disabled={saving}>
          <X className="w-3.5 h-3.5 text-slate-500" />
        </Button>
      </div>
    );
  }

  return (
    <Select
      value={value || ""}
      disabled={disabled}
      onValueChange={(v) => (v === NEW_VALUE ? setCreating(true) : onChange(v))}
    >
      <SelectTrigger className={`h-8 ${className}`} data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allOptions.map((opt) => (
          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
        ))}
        {onCreate && (
          <SelectItem value={NEW_VALUE} className="text-blue-600 font-medium border-t mt-1 pt-1" data-testid={testId ? `${testId}-new-option` : undefined}>
            <span className="flex items-center gap-1"><Plus className="w-3 h-3" /> {createLabel}</span>
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
};

export default CreatableSelect;
